import { sendPOSTRequest } from "../requestHelper/sendRequest";

export async function uploadAllToS3(
	filesToUpload: Array<{
		file: File;
		progress: number;
	}>,
	uploadUrls: Array<{ fileName: string; presignedPost: AWS.S3.PresignedPost }>,
	setFileProgress: (index: number, progress: number) => void
) {
	const uploadPromises: Promise<void>[] = []; // Array to hold promises for all uploads

	for (let i = 0; i < filesToUpload.length; i++) {
		const file: { file: File; progress: number } = filesToUpload[i];

		const uploadPresignedPost: AWS.S3.PresignedPost | undefined =
			uploadUrls.find((element) => {
				return element.fileName == file.file.name;
			})?.presignedPost;
		if (uploadPresignedPost) {
			uploadPromises.push(
				uploadFile(file, uploadPresignedPost, i, setFileProgress)
			);
		} else {
			setFileProgress(i, -1);
		}
	}

	const results = await Promise.allSettled(uploadPromises);

	return results.map((result) => {
		return result.status === "fulfilled";
	});
}

export async function uploadFile(
	file: { file: File; progress: number },
	presignedPost: AWS.S3.PresignedPost,
	index: number,
	setFileProgress: (index: number, progress: number) => void
) {
	const formData = new FormData();

	// Append the required fields from the presignedPost
	for (const field in presignedPost.fields) {
		formData.append(field, presignedPost.fields[field]);
	}

	// Append the file to the form data
	formData.append("file", file.file);
	const xhr = new XMLHttpRequest();

	return new Promise<void>((resolve, reject) => {
		// Set up progress event listener
		xhr.upload.addEventListener("progress", (event) => {
			if (event.lengthComputable) {
				const percentComplete = Math.round((event.loaded / event.total) * 100);
				setFileProgress(index, percentComplete); // Update progress
			}
		});

		// Set up the load event listener to handle the completion of the upload
		xhr.addEventListener("load", () => {
			if (xhr.status === 200 || xhr.status === 204) {
				resolve(); // Resolve promise if upload is successful
			} else {
				setFileProgress(index, -1);
				reject(new Error(`Upload failed for file: ${file.file.name}`));
			}
		});

		// Set up error event listener
		xhr.addEventListener("error", (err) => {
			setFileProgress(index, -1);
			reject(new Error(`Upload error for file: ${file.file.name}`));
		});

		// Open the request (POST method)
		xhr.open("POST", presignedPost.url, true);

		// Send the request with the form data
		xhr.send(formData);
	});
}

//downloads
export async function downloadSpecificationFile(specificationId: string) {
	const response = await sendPOSTRequest(
		"/specifications/download-specification",
		{
			specificationId,
		}
	);
	const fileAndsignedURL: {
		fileName: string;
		format: string;
		signedURL: string;
	} = response.data.fileAndsignedURL;

	const downloadResults: Array<{
		fullName: string;
		success: boolean;
	}> = await handleDownloadAll([fileAndsignedURL]);

	return downloadResults[0].success;
}

export async function handleDownloadAll(
	fileAndSignedURLs: Array<{
		fileName: string;
		format: string;
		signedURL: string;
	}>
) {
	const statusPromises = fileAndSignedURLs.map(async (fileInfo) => {
		try {
			// Check if the file is accessible by fetching the signed URL
			const response = await fetch(fileInfo.signedURL);

			if (response.ok) {
				// Proceed with downloading the file if it's accessible
				const blob = await response.blob();

				// Create an object URL for the Blob
				const link = document.createElement("a");
				link.href = URL.createObjectURL(blob); // Use Blob URL instead of direct signed URL
				link.download = `${fileInfo.fileName}.${fileInfo.format}`; // Set the download attribute with file name and format

				// Trigger the download by programmatically clicking the link
				link.click();

				// Clean up the object URL after the download
				URL.revokeObjectURL(link.href);

				return {
					fullName: `${fileInfo.fileName}.${fileInfo.format}`,
					success: true,
				};
			} else {
				return {
					fullName: `${fileInfo.fileName}.${fileInfo.format}`,
					success: false,
				};
			}
		} catch (error) {
			return {
				fullName: `${fileInfo.fileName}.${fileInfo.format}`,
				success: false,
			};
		}
	});

	const results = await Promise.allSettled(statusPromises);

	// You can map the results to a more readable format if needed
	const downloadResults = results.map((result, index) => {
		if (result.status === "fulfilled") {
			return result.value; // success result
		} else {
			return {
				fullName: `${fileAndSignedURLs[index].fileName}.${fileAndSignedURLs[index].format}`,
				success: false,
			}; // failure result
		}
	});

	return downloadResults;
}
